import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { inventoryApi } from '@/services/inventory.api';

/**
 * Low stock and overdue loans, at a glance — the same "surface it back to the
 * people who'd act on it" gap `BirthdaysWidget` closes for birthdays, applied
 * to a reorder threshold and a due-back date nobody was otherwise watching.
 */
export function InventoryAlertsWidget() {
  const { t } = useI18n();
  const { can } = useAuth();

  const alerts = useQuery({
    queryKey: ['dashboard', 'inventory-alerts'],
    queryFn: ({ signal }) => inventoryApi.getAlerts(signal),
    enabled: can('inventory.item.read'),
  });

  if (!can('inventory.item.read')) return null;

  const lowStock = alerts.data?.lowStockItems ?? [];
  const overdue = alerts.data?.overdueCheckouts ?? [];

  if (alerts.data && lowStock.length === 0 && overdue.length === 0) return null;

  return (
    <div className="dashboard-category">
      <p className="dashboard-category-label">{t('inventory.alerts.title')}</p>

      {!alerts.data ? null : (
        <ul className="dashboard-birthdays-list">
          {lowStock.map((item) => (
            <li className="dashboard-birthday-row" key={`low-stock-${item.id}`}>
              <div className="dashboard-birthday-info">
                <Link to={`/inventory/${item.id}`} className="dashboard-birthday-name">
                  {item.name}
                </Link>
                <span className="dashboard-birthday-age">{t('inventory.alerts.lowStock')}</span>
              </div>
              <span className="dashboard-birthday-when" dir="ltr">
                {item.quantityOnHand} / {item.minQuantity} {item.unit}
              </span>
            </li>
          ))}
          {overdue.map((checkout) => (
            <li className="dashboard-birthday-row" key={`overdue-${checkout.id}`}>
              <div className="dashboard-birthday-info">
                <Link to={`/inventory/${checkout.itemId}`} className="dashboard-birthday-name">
                  {checkout.itemName}
                </Link>
                <span className="dashboard-birthday-age">
                  {t('inventory.alerts.overdueWith', {
                    values: { name: checkout.borrowerDisplayName },
                  })}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
